import type { ZodType } from 'zod';
import { isoDateRegexString, isoTimeRegexString } from '@erikdakoda/i18n/shared/date';

/**
 * Minimal view of a Zod 4 check def (`check._zod.def`).
 * Zod 4 removed the v3 `check.kind`/`check.value` shape; checks now expose a
 * discriminant `check` plus per-check payload (`minimum`, `maximum`, `format`, `pattern`).
 */
interface ZodCheckDef {
  readonly check?: string;
  readonly format?: string;
  readonly pattern?: RegExp;
  readonly minimum?: number;
  readonly maximum?: number;
}

/**
 * Minimal view of a Zod 4 internal schema def (`schema._zod.def`).
 * Zod 4 removed the v3 `_def.typeName` / `_def.values` / `ZodString.isDatetime`
 * accessors; the canonical internals now live under `_zod.def`. Typed narrowly
 * (no `any`) so these introspection helpers can walk the def tree.
 */
interface ZodInternalDef {
  readonly type: string;
  readonly innerType?: ZodType;
  readonly options?: readonly ZodType[];
  readonly entries?: Readonly<Record<string, unknown>>;
  readonly format?: string;
  readonly checks?: readonly { readonly _zod?: { readonly def?: ZodCheckDef } }[];
}

/** Reads a Zod 4 schema's internal def (`schema._zod.def`) without using `any`. */
function zodDef(schema: ZodType): ZodInternalDef {
  return (schema as unknown as { _zod: { def: ZodInternalDef } })._zod.def;
}

/** Unwraps optional/nullable/default/etc. wrappers to the underlying schema and its def. */
function unwrapZodSchema(schema: ZodType): { schema: ZodType; def: ZodInternalDef } {
  let current = schema;
  let def = zodDef(current);
  while (def.innerType) {
    current = def.innerType;
    def = zodDef(current);
  }
  return { schema: current, def };
}

/**
 * Collapses a `DateTime` field expressed as `z.union([z.date(), z.string()])` to `'date'`.
 *
 * ZenStack 3's schema factory types a DateTime column as that union so it accepts both a `Date` and
 * an ISO string. Reported verbatim it is `'union'`, which matches no branch in the data grid's
 * filter converter — every date filter would silently stop matching. Returns undefined for any
 * other union (notably `Json`, which has no date member and no converter branch either way).
 */
function dateUnionType(def: ZodInternalDef): string | undefined {
  if (def.type !== 'union') return undefined;
  const hasDateMember = unionHasDateMember(def);
  return hasDateMember ? 'date' : undefined;
}

function unionHasDateMember(def: ZodInternalDef): boolean {
  return def.options?.some((option) => unwrapZodSchema(option).def.type === 'date') ?? false;
}

/**
 * True when the field schema is a ZenStack `DateTime` column (`z.union` that includes `z.date()`).
 * `typeOfZodSchema` reports these as `'date'` for the data grid; editor date pickers must still
 * emit a `Date`, not an ISO date string.
 * @param fieldSchema A Zod field schema (optional/nullable wrappers are unwrapped).
 * @returns Whether the picker should emit `Date` values.
 */
export function zodSchemaIsDateTimeUnion(fieldSchema: ZodType): boolean {
  const { def } = unwrapZodSchema(fieldSchema);
  if (def.type !== 'union') {
    return false;
  }
  return unionHasDateMember(def);
}

const isoStringFormats = new Set(['datetime', 'date', 'time']);

/**
 * Returns the ISO string format (`datetime` | `date` | `time`) of a Zod string schema, or
 * undefined. Covers both `z.iso.*()` (format on the def) and `z.string().date()` etc.
 * (format on a `string_format` check).
 */
function isoStringFormat(def: ZodInternalDef): string | undefined {
  if (typeof def.format === 'string' && isoStringFormats.has(def.format)) {
    return def.format;
  }
  for (const check of def.checks ?? []) {
    const format = check._zod?.def?.format;
    if (typeof format === 'string' && isoStringFormats.has(format)) {
      return format;
    }
  }
  return undefined;
}

/** Returns the source of the first regex (`.regex()`) check on a Zod string schema, or undefined. */
function firstRegexSource(def: ZodInternalDef): string | undefined {
  for (const check of def.checks ?? []) {
    const pattern = check._zod?.def?.pattern;
    if (pattern instanceof RegExp) {
      return pattern.toString().replace(/^\/|\/$/g, '');
    }
  }
  return undefined;
}

/**
 * Returns the schema of the given field from a Zod object schema.
 * @param schema A Zod object schema.
 * @param fieldName The name of the field to get the schema for.
 * @returns The field schema, or undefined if the field is not found.
 */
export function getFieldSchema(schema: ZodType, fieldName: string): ZodType | undefined {
  const shape = (schema as unknown as { shape?: Record<string, ZodType> }).shape;
  return shape?.[fieldName];
}

/**
 * Returns the type name of a Zod schema.
 * @param fieldSchema A Zod field schema.
 * @returns The type name of the Zod field schema. (string|number|boolean|date|datetime|time|...)
 */
export function typeOfZodSchema(fieldSchema: ZodType): string {
  const { def } = unwrapZodSchema(fieldSchema);
  const typeName = dateUnionType(def) ?? def.type;

  if (typeName === 'string') {
    const format = isoStringFormat(def);
    if (format !== undefined) {
      return format;
    }
    const regexSource = firstRegexSource(def);
    if (regexSource === isoDateRegexString) {
      return 'date';
    }
    if (regexSource === isoTimeRegexString) {
      return 'time';
    }
  }
  return typeName;
}

/**
 * Returns true when a Zod number schema restricts values to integers (`.int()` / `z.int()`).
 */
export function integerZodSchema(fieldSchema: ZodType): boolean {
  const { def } = unwrapZodSchema(fieldSchema);
  if (def.type !== 'number') {
    return false;
  }
  if (typeof def.format === 'string' && def.format.includes('int')) {
    return true;
  }
  return (def.checks ?? []).some((check) => {
    const format = check._zod?.def?.format;
    return typeof format === 'string' && format.includes('int');
  });
}

/** Min/max length constraints read from a Zod string schema. */
export interface ZodStringLengthLimits {
  minLength?: number;
  maxLength?: number;
}

/**
 * Reads min/max length constraints from a Zod string schema (unwraps optional/nullable first).
 * @param fieldSchema A Zod field schema.
 * @returns The min/max length, or an empty object when the schema is not a length-limited string.
 */
export function stringLengthLimitsOfZodSchema(fieldSchema: ZodType): ZodStringLengthLimits {
  const { def } = unwrapZodSchema(fieldSchema);
  if (def.type !== 'string') {
    return {};
  }
  const limits: ZodStringLengthLimits = {};
  for (const check of def.checks ?? []) {
    const checkDef = check._zod?.def;
    if (checkDef?.check === 'min_length' && typeof checkDef.minimum === 'number') {
      limits.minLength = checkDef.minimum;
    }
    if (checkDef?.check === 'max_length' && typeof checkDef.maximum === 'number') {
      limits.maxLength = checkDef.maximum;
    }
  }
  return limits;
}

/**
 * Returns the option values of a Zod enum schema.
 * @param fieldSchema A Zod field schema.
 * @returns The enum option values, or an empty array when the schema is not an enum.
 */
export function optionsOfZodEnum(fieldSchema: ZodType): string[] {
  const { def } = unwrapZodSchema(fieldSchema);
  if (def.type === 'enum') {
    return Object.values(def.entries ?? {}).map((value) => String(value));
  }
  return [];
}

/**
 * Determines if a Zod field schema is optional.
 * @param fieldSchema A Zod field schema, or `undefined` when the field has no schema (e.g. a
 * relation or virtual field, which the ZenStack 3 schema factory does not emit).
 * @returns True, if the schema is optional. A missing schema imposes no requirement, so returns true.
 */
export function optionalZodSchema(fieldSchema: ZodType | undefined): boolean {
  if (!fieldSchema) return true;
  const parsed = fieldSchema.safeParse(null);
  const optional = parsed.success === true;
  return optional;
}

/**
 * Determines if a Zod field schema accepts `undefined` (i.e. is `.optional()`).
 * Replaces the deprecated `ZodType.isOptional()` method.
 * @param fieldSchema A Zod field schema, or `undefined` when the field has no schema (e.g. a
 * relation or virtual field, which the ZenStack 3 schema factory does not emit).
 * @returns True, if the schema accepts `undefined`. A missing schema imposes no requirement, so
 * returns true.
 */
export function isOptionalZodSchema(fieldSchema: ZodType | undefined): boolean {
  return fieldSchema ? fieldSchema.safeParse(undefined).success : true;
}

/**
 * Returns the field schema or the first field schema of a Zod union.
 * @param fieldSchema A Zod field schema.
 * @returns The field schema passed or the first field schema of a Zod union, or null.
 */
export function firstObjectInZodUnion(fieldSchema: ZodType): ZodType | null {
  const { schema, def } = unwrapZodSchema(fieldSchema);
  if (def.type === 'object') {
    return schema;
  } else if (def.type === 'union') {
    return def.options?.[0] ?? null;
  } else {
    return null;
  }
}
